import React, {
    useEffect,
    useMemo,
    useState
}
from "react";

import { FontAwesomeIcon }
from "@fortawesome/react-fontawesome";

import {
    faChevronRight,
    faMagnifyingGlass,
    faSpinner,
    faXmark
}
from "@fortawesome/free-solid-svg-icons";

import ScrollableList
from "../../../../components/ScrollableList";


export default function DashboardInsightResultsDrawer({
    display,
    title,
    insights = [],
    closeFunction,
    insightClickFunction
})
{

    //--------------------------------------------------------
    // State
    //--------------------------------------------------------

    const [search, setSearch] =
        useState("");


    //--------------------------------------------------------
    // Reset search when drawer opens
    //--------------------------------------------------------

    useEffect(
        () =>
        {
            if(display)
            {
                setSearch("");
            }
        },
        [
            display,
            title
        ]
    );


    //--------------------------------------------------------
    // Search results
    //--------------------------------------------------------

    const filteredInsights =
        useMemo(
            () =>
            {
                const value =
                    search
                        .trim()
                        .toLowerCase();


                if(value === "")
                {
                    return insights;
                }


                return insights.filter(
                    (insight) =>
                    {
                        return (
                            contains(
                                insight.title,
                                value
                            )
                            ||
                            contains(
                                insight.description,
                                value
                            )
                            ||
                            contains(
                                insight.category,
                                value
                            )
                            ||
                            contains(
                                insight.subcategory,
                                value
                            )
                            ||
                            contains(
                                insight.severity,
                                value
                            )
                            ||
                            contains(
                                insight.urgency,
                                value
                            )
                            ||
                            contains(
                                insight.complianceStatus,
                                value
                            )
                            ||
                            contains(
                                insight.regulation,
                                value
                            )
                            ||
                            contains(
                                insight.businessPriority,
                                value
                            )
                            ||
                            contains(
                                insight.operationalDisruption,
                                value
                            )
                            ||
                            contains(
                                insight.status,
                                value
                            )
                        );
                    }
                );
            },
            [
                insights,
                search
            ]
        );


    //--------------------------------------------------------
    // Summary
    //--------------------------------------------------------

    const summary =
        useMemo(
            () =>
            {
                return {
                    subcategories:
                        countDistinct(
                            filteredInsights,
                            "subcategory"
                        ),

                    severities:
                        countDistinct(
                            filteredInsights,
                            "severity"
                        ),

                    compliance:
                        countDistinct(
                            filteredInsights,
                            "complianceStatus"
                        )
                };
            },
            [
                filteredInsights
            ]
        );


    //--------------------------------------------------------
    // Open insight
    //--------------------------------------------------------

    function openInsight(insight)
    {
        if(insightClickFunction)
        {
            insightClickFunction(
                insight
            );
        }
    }


    //--------------------------------------------------------
    // Render
    //--------------------------------------------------------

    return (

        <>

            {/*----------------------------------------------*/}
            {/* BACKDROP                                     */}
            {/*----------------------------------------------*/}

            <div
                style={{
                    ...backdropStyle,

                    opacity:
                        display
                            ?
                            1
                            :
                            0,

                    pointerEvents:
                        display
                            ?
                            "auto"
                            :
                            "none"
                }}

                onClick={
                    closeFunction
                }
            />


            {/*----------------------------------------------*/}
            {/* DRAWER                                       */}
            {/*----------------------------------------------*/}

            <div
                style={{
                    ...drawerStyle,

                    transform:
                        display
                            ?
                            "translateX(0)"
                            :
                            "translateX(100%)"
                }}
            >

                {/*------------------------------------------*/}
                {/* HEADER                                   */}
                {/*------------------------------------------*/}

                <div style={headerStyle}>

                    <div style={headerTextContainerStyle}>

                        <div style={eyebrowStyle}>
                            Insight Results
                        </div>

                        <div style={titleStyle}>
                            {
                                title
                                ||
                                "Portfolio Insights"
                            }
                        </div>

                    </div>


                    <button
                        type="button"

                        style={closeButtonStyle}

                        onClick={
                            closeFunction
                        }
                    >
                        <FontAwesomeIcon
                            icon={faXmark}
                        />
                    </button>

                </div>


                {/*------------------------------------------*/}
                {/* RESULT SUMMARY                           */}
                {/*------------------------------------------*/}

                <div style={summaryContainerStyle}>

                    <div style={resultCountContainerStyle}>

                        <div style={resultCountStyle}>
                            {
                                filteredInsights
                                    .length
                                    .toLocaleString()
                            }
                        </div>

                        <div style={resultCountLabelStyle}>
                            Matching Insights
                        </div>

                    </div>


                    <div style={summaryStatsStyle}>

                        <SummaryStat
                            value={
                                summary.subcategories
                            }
                            label="Subcategories"
                        />

                        <SummaryStat
                            value={
                                summary.severities
                            }
                            label="Severities"
                        />

                        <SummaryStat
                            value={
                                summary.compliance
                            }
                            label="Compliance States"
                        />

                    </div>

                </div>


                {/*------------------------------------------*/}
                {/* SEARCH                                   */}
                {/*------------------------------------------*/}

                <div style={searchContainerStyle}>

                    <div style={searchBoxStyle}>

                        <FontAwesomeIcon
                            icon={
                                faMagnifyingGlass
                            }

                            style={
                                searchIconStyle
                            }
                        />

                        <input
                            type="text"

                            value={
                                search
                            }

                            placeholder={
                                "Search matching insights..."
                            }

                            onChange={(event) =>
                            {
                                setSearch(
                                    event.target.value
                                );
                            }}

                            style={
                                searchInputStyle
                            }
                        />

                        {
                            search
                            &&
                            <button
                                type="button"

                                style={
                                    clearSearchButtonStyle
                                }

                                onClick={() =>
                                {
                                    setSearch("");
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={
                                        faXmark
                                    }
                                />
                            </button>
                        }

                    </div>

                </div>


                {/*------------------------------------------*/}
                {/* RESULT LABEL                             */}
                {/*------------------------------------------*/}

                <div style={listHeaderStyle}>

                    <div>
                        Results
                    </div>

                    <div style={listHeaderCountStyle}>
                        {
                            filteredInsights
                                .length
                                .toLocaleString()
                        }
                    </div>

                </div>


                {/*------------------------------------------*/}
                {/* LIST                                     */}
                {/*------------------------------------------*/}

                <div style={listContainerStyle}>

                    <ScrollableList
                        height="calc(100vh - 245px)"
                    >

                        <div style={listInnerStyle}>

                            {
                                filteredInsights.length === 0
                                ?
                                    <EmptyResults />
                                :
                                    filteredInsights.map(
                                        (insight) =>
                                        {
                                            return (

                                                <InsightCard
                                                    key={
                                                        insight.id
                                                    }

                                                    insight={
                                                        insight
                                                    }

                                                    clickFunction={
                                                        openInsight
                                                    }
                                                />

                                            );
                                        }
                                    )
                            }

                        </div>

                    </ScrollableList>

                </div>

            </div>

        </>

    );
}


//--------------------------------------------------------
// Insight card
//--------------------------------------------------------

function InsightCard({
    insight,
    clickFunction
})
{

    const complianceStyle =
        getComplianceStyle(
            insight.complianceStatus
        );


    const severityStyle =
        getSeverityStyle(
            insight.severity
        );


    return (

        <div style={cardStyle}>

            {/*----------------------------------------------*/}
            {/* TOP ROW                                      */}
            {/*----------------------------------------------*/}

            <div style={cardTopRowStyle}>

                <div style={cardHeadingContainerStyle}>

                    <div style={cardTitleStyle}>
                        {
                            insight.title
                            ||
                            "Untitled Insight"
                        }
                    </div>


                    {
                        insight.subcategory
                        &&
                        <div style={subcategoryStyle}>
                            {
                                formatDisplayValue(
                                    insight.subcategory
                                )
                            }
                        </div>
                    }

                </div>


                {
                    insight.severity
                    &&
                    <div
                        style={{
                            ...badgeStyle,
                            ...severityStyle
                        }}
                    >
                        {
                            formatDisplayValue(
                                insight.severity
                            )
                        }
                    </div>
                }

            </div>


            {/*----------------------------------------------*/}
            {/* DESCRIPTION                                  */}
            {/*----------------------------------------------*/}

            {
                insight.description
                &&
                <div style={descriptionStyle}>
                    {
                        insight.description
                    }
                </div>
            }


            {/*----------------------------------------------*/}
            {/* METRICS                                      */}
            {/*----------------------------------------------*/}

            <div style={metricGridStyle}>

                <Metric
                    label="Risk"
                    value={
                        insight.riskScore
                        ??
                        "—"
                    }
                />

                <Metric
                    label="Urgency"
                    value={
                        formatDisplayValue(
                            insight.urgency
                        )
                    }
                />

                <Metric
                    label="Confidence"
                    value={
                        insight.confidence != null
                            ?
                            formatConfidence(
                                insight.confidence
                            )
                            :
                            "—"
                    }
                />

                <Metric
                    label="Priority"
                    value={
                        formatDisplayValue(
                            insight.businessPriority
                        )
                    }
                />

            </div>


            {/*----------------------------------------------*/}
            {/* FOOTER                                       */}
            {/*----------------------------------------------*/}

            <div style={cardFooterStyle}>

                <div style={badgeContainerStyle}>

                    <div
                        style={{
                            ...badgeStyle,
                            ...complianceStyle
                        }}
                    >
                        {
                            formatDisplayValue(
                                insight.complianceStatus
                            )
                        }
                    </div>


                    {
                        insight.operationalDisruption
                        &&
                        <div style={secondaryBadgeStyle}>
                            {
                                formatDisplayValue(
                                    insight.operationalDisruption
                                )
                            }
                        </div>
                    }

                </div>


                <button
                    type="button"

                    style={openButtonStyle}

                    onClick={() =>
                    {
                        clickFunction(
                            insight
                        );
                    }}
                >

                    View

                    <FontAwesomeIcon
                        icon={
                            faChevronRight
                        }
                    />

                </button>

            </div>

        </div>

    );
}


//--------------------------------------------------------
// Metric
//--------------------------------------------------------

function Metric({
    label,
    value
})
{

    return (

        <div style={metricStyle}>

            <div style={metricLabelStyle}>
                {label}
            </div>

            <div style={metricValueStyle}>
                {
                    value === null
                    ||
                    value === undefined
                    ||
                    value === ""
                        ?
                        "—"
                        :
                        value
                }
            </div>

        </div>

    );
}


//--------------------------------------------------------
// Summary stat
//--------------------------------------------------------

function SummaryStat({
    value,
    label
})
{

    return (

        <div style={summaryStatStyle}>

            <div style={summaryStatValueStyle}>
                {
                    value
                        .toLocaleString()
                }
            </div>

            <div style={summaryStatLabelStyle}>
                {label}
            </div>

        </div>

    );
}


//--------------------------------------------------------
// Empty results
//--------------------------------------------------------

function EmptyResults()
{

    return (

        <div style={emptyStyle}>

            <FontAwesomeIcon
                icon={faMagnifyingGlass}

                style={emptyIconStyle}
            />

            <div style={emptyTitleStyle}>
                No matching insights
            </div>

            <div style={emptyTextStyle}>
                Try changing your search.
            </div>

        </div>

    );
}


//--------------------------------------------------------
// Helpers
//--------------------------------------------------------

function contains(
    value,
    search)
{
    if(
        value === null
        ||
        value === undefined
    )
    {
        return false;
    }

    return String(value)
        .toLowerCase()
        .includes(search);
}


function countDistinct(
    items,
    key)
{
    const values =
        new Set();


    items.forEach(
        (item) =>
        {
            const value =
                normaliseValue(
                    item[key]
                );

            if(
                value
                &&
                value !== "not assessed"
            )
            {
                values.add(
                    value
                );
            }
        }
    );


    return values.size;
}


function normaliseValue(value)
{
    if(
        value === null
        ||
        value === undefined
        ||
        String(value).trim() === ""
    )
    {
        return "not assessed";
    }

    return String(value)
        .trim()
        .toLowerCase()
        .replace(/_/g, " ");
}


function formatDisplayValue(value)
{
    if(
        value === null
        ||
        value === undefined
        ||
        String(value).trim() === ""
    )
    {
        return "—";
    }


    const formatted =
        String(value)
            .trim()
            .replace(/_/g, " ")
            .toLowerCase();


    return formatted.replace(
        /\b\w/g,
        (character) =>
            character.toUpperCase()
    );
}


function formatConfidence(value)
{
    const number =
        Number(value);


    if(Number.isNaN(number))
    {
        return "—";
    }


    return number.toLocaleString(
        undefined,
        {
            maximumFractionDigits: 1
        }
    )
    +
    "%";
}


//--------------------------------------------------------
// Severity style
//--------------------------------------------------------

function getSeverityStyle(severity)
{
    const value =
        normaliseValue(
            severity
        );


    if(value === "critical")
    {
        return {
            color: "#ff7b80",

            background:
                "rgba(239,63,69,0.15)",

            border:
                "1px solid rgba(239,63,69,0.30)"
        };
    }


    if(value === "high")
    {
        return {
            color: "#ff985c",

            background:
                "rgba(255,107,19,0.15)",

            border:
                "1px solid rgba(255,107,19,0.30)"
        };
    }


    if(value === "medium")
    {
        return {
            color: "#f5c451",

            background:
                "rgba(242,179,33,0.14)",

            border:
                "1px solid rgba(242,179,33,0.28)"
        };
    }


    if(value === "low")
    {
        return {
            color: "#59d48c",

            background:
                "rgba(32,161,90,0.15)",

            border:
                "1px solid rgba(32,161,90,0.28)"
        };
    }


    return {
        color: "#b8c2cf",

        background:
            "rgba(148,163,184,0.12)",

        border:
            "1px solid rgba(148,163,184,0.20)"
    };
}


//--------------------------------------------------------
// Compliance style
//--------------------------------------------------------

function getComplianceStyle(status)
{
    const value =
        normaliseValue(
            status
        );


    if(
        value.includes("non-compliant")
        ||
        value.includes("non compliant")
    )
    {
        return {
            color: "#ff7b80",

            background:
                "rgba(239,68,68,0.14)",

            border:
                "1px solid rgba(239,68,68,0.28)"
        };
    }


    if(
        value.includes("potential")
        ||
        value.includes("concern")
    )
    {
        return {
            color: "#ff985c",

            background:
                "rgba(249,115,22,0.14)",

            border:
                "1px solid rgba(249,115,22,0.28)"
        };
    }


    if(
        value.includes("compliant")
    )
    {
        return {
            color: "#59d48c",

            background:
                "rgba(34,197,94,0.14)",

            border:
                "1px solid rgba(34,197,94,0.27)"
        };
    }


    return {
        color: "#b8c2cf",

        background:
            "rgba(148,163,184,0.12)",

        border:
            "1px solid rgba(148,163,184,0.20)"
    };
}


//--------------------------------------------------------
// Styles
//--------------------------------------------------------

const backdropStyle =
{
    position: "fixed",

    inset: 0,

    zIndex: 1190,

    background:
        "rgba(5,8,13,0.16)",

    backdropFilter:
        "blur(1px)",

    transition:
        "opacity 0.35s ease"
};


const drawerStyle =
{
    position: "fixed",

    top: 0,
    right: 0,
    bottom: 0,

    width: "490px",
    height: "100vh",

    zIndex: 1200,

    display: "flex",
    flexDirection: "column",

    background:
        "linear-gradient(145deg, #292e35 0%, #171a1f 48%, #090b0e 100%)",

    borderLeft:
        "1px solid rgba(255,255,255,0.10)",

    boxShadow:
        "-18px 0 50px rgba(0,0,0,0.28)",

    overflow: "hidden",

    transition:
        "transform 0.65s cubic-bezier(0.22, 1, 0.36, 1)"
};


//--------------------------------------------------------
// Header
//--------------------------------------------------------

const headerStyle =
{
    minHeight: "82px",

    display: "flex",
    alignItems: "center",

    padding: "0 22px",

    borderBottom:
        "1px solid rgba(255,255,255,0.09)",

    flexShrink: 0
};


const headerTextContainerStyle =
{
    flex: 1,

    minWidth: 0
};


const eyebrowStyle =
{
    color: "#62a3ff",

    fontSize: "9px",
    fontWeight: "800",

    letterSpacing: "1.4px",

    textTransform: "uppercase"
};


const titleStyle =
{
    marginTop: "4px",

    overflow: "hidden",

    color: "#ffffff",

    fontSize: "17px",
    fontWeight: "700",

    whiteSpace: "nowrap",

    textOverflow: "ellipsis"
};


const closeButtonStyle =
{
    width: "36px",
    height: "36px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    marginLeft: "14px",

    color: "#aab4c1",

    background:
        "rgba(255,255,255,0.06)",

    border:
        "1px solid rgba(255,255,255,0.10)",

    borderRadius: "9px",

    cursor: "pointer"
};


//--------------------------------------------------------
// Summary
//--------------------------------------------------------

const summaryContainerStyle =
{
    margin:
        "18px 18px 0 18px",

    padding: "16px",

    background:
        "linear-gradient(145deg, rgba(255,255,255,0.075) 0%, rgba(255,255,255,0.035) 100%)",

    border:
        "1px solid rgba(255,255,255,0.09)",

    borderRadius: "12px",

    boxShadow:
        "0 10px 24px rgba(0,0,0,0.18)"
};


const resultCountContainerStyle =
{
    display: "flex",
    alignItems: "center"
};


const resultCountStyle =
{
    color: "#62a3ff",

    fontSize: "30px",
    lineHeight: 1,

    fontWeight: "850"
};


const resultCountLabelStyle =
{
    marginLeft: "11px",

    color: "#d5dce6",

    fontSize: "10px",
    fontWeight: "800",

    letterSpacing: "0.8px",

    textTransform: "uppercase"
};


const summaryStatsStyle =
{
    display: "grid",

    gridTemplateColumns:
        "repeat(3, minmax(0, 1fr))",

    marginTop: "14px",

    paddingTop: "13px",

    borderTop:
        "1px solid rgba(255,255,255,0.08)"
};


const summaryStatStyle =
{
    minWidth: 0
};


const summaryStatValueStyle =
{
    color: "#ffffff",

    fontSize: "14px",
    fontWeight: "750"
};


const summaryStatLabelStyle =
{
    marginTop: "2px",

    overflow: "hidden",

    color: "#778496",

    fontSize: "8px",
    fontWeight: "700",

    whiteSpace: "nowrap",

    textOverflow: "ellipsis",

    textTransform: "uppercase",

    letterSpacing: "0.5px"
};


//--------------------------------------------------------
// Search
//--------------------------------------------------------

const searchContainerStyle =
{
    padding:
        "14px 18px 10px 18px",

    flexShrink: 0
};


const searchBoxStyle =
{
    height: "40px",

    display: "flex",
    alignItems: "center",

    padding: "0 12px",

    background:
        "rgba(255,255,255,0.065)",

    border:
        "1px solid rgba(255,255,255,0.11)",

    borderRadius: "9px"
};


const searchIconStyle =
{
    marginRight: "9px",

    color: "#667386",

    fontSize: "11px"
};


const searchInputStyle =
{
    flex: 1,

    minWidth: 0,

    height: "100%",

    padding: 0,

    color: "#ffffff",

    background: "transparent",

    border: "none",

    outline: "none",

    fontSize: "11px"
};


const clearSearchButtonStyle =
{
    width: "26px",
    height: "26px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    padding: 0,

    color: "#8490a0",

    background: "transparent",

    border: "none",

    cursor: "pointer"
};


//--------------------------------------------------------
// List
//--------------------------------------------------------

const listHeaderStyle =
{
    display: "flex",
    alignItems: "center",

    padding:
        "5px 22px 8px 22px",

    color: "#758195",

    fontSize: "9px",
    fontWeight: "800",

    letterSpacing: "1px",

    textTransform: "uppercase"
};


const listHeaderCountStyle =
{
    marginLeft: "auto"
};


const listContainerStyle =
{
    flex: 1,

    minHeight: 0,

    padding:
        "0 8px 0 10px"
};


const listInnerStyle =
{
    padding:
        "0 8px 24px 8px"
};


//--------------------------------------------------------
// Card
//--------------------------------------------------------

const cardStyle =
{
    marginBottom: "10px",

    padding: "14px",

    background:
        "rgba(255,255,255,0.055)",

    border:
        "1px solid rgba(255,255,255,0.085)",

    borderRadius: "11px",

    boxShadow:
        "0 5px 14px rgba(0,0,0,0.10)"
};


const cardTopRowStyle =
{
    display: "flex",

    alignItems: "flex-start",

    gap: "10px"
};


const cardHeadingContainerStyle =
{
    flex: 1,

    minWidth: 0
};


const cardTitleStyle =
{
    color: "#ffffff",

    fontSize: "12px",
    fontWeight: "700",

    lineHeight: "1.4"
};


const subcategoryStyle =
{
    marginTop: "4px",

    color: "#7f8da0",

    fontSize: "9px",
    fontWeight: "700",

    letterSpacing: "0.4px",

    textTransform: "uppercase"
};


const descriptionStyle =
{
    marginTop: "10px",

    display: "-webkit-box",

    overflow: "hidden",

    color: "#b6c0cd",

    fontSize: "11px",
    lineHeight: "1.5",

    WebkitBoxOrient: "vertical",

    WebkitLineClamp: 3
};


//--------------------------------------------------------
// Metrics
//--------------------------------------------------------

const metricGridStyle =
{
    display: "grid",

    gridTemplateColumns:
        "repeat(4, minmax(0, 1fr))",

    marginTop: "12px",

    padding:
        "10px 0",

    borderTop:
        "1px solid rgba(255,255,255,0.07)",

    borderBottom:
        "1px solid rgba(255,255,255,0.07)"
};


const metricStyle =
{
    minWidth: 0
};


const metricLabelStyle =
{
    color: "#657286",

    fontSize: "8px",
    fontWeight: "700",

    letterSpacing: "0.4px",

    textTransform: "uppercase"
};


const metricValueStyle =
{
    marginTop: "3px",

    overflow: "hidden",

    color: "#e5eaf0",

    fontSize: "10px",
    fontWeight: "700",

    whiteSpace: "nowrap",

    textOverflow: "ellipsis"
};


//--------------------------------------------------------
// Badges
//--------------------------------------------------------

const badgeStyle =
{
    flexShrink: 0,

    padding: "4px 7px",

    borderRadius: "6px",

    fontSize: "8px",
    fontWeight: "800",

    letterSpacing: "0.4px",

    textTransform: "uppercase"
};


const secondaryBadgeStyle =
{
    padding: "4px 7px",

    color: "#a9b5c5",

    background:
        "rgba(148,163,184,0.10)",

    border:
        "1px solid rgba(148,163,184,0.17)",

    borderRadius: "6px",

    fontSize: "8px",
    fontWeight: "750",

    textTransform: "uppercase"
};


const badgeContainerStyle =
{
    flex: 1,

    minWidth: 0,

    display: "flex",
    alignItems: "center",

    flexWrap: "wrap",

    gap: "6px"
};


//--------------------------------------------------------
// Footer
//--------------------------------------------------------

const cardFooterStyle =
{
    display: "flex",
    alignItems: "center",

    marginTop: "11px"
};


const openButtonStyle =
{
    display: "flex",
    alignItems: "center",

    gap: "7px",

    marginLeft: "10px",

    padding:
        "6px 9px",

    color: "#62a3ff",

    background:
        "rgba(79,141,255,0.08)",

    border:
        "1px solid rgba(79,141,255,0.17)",

    borderRadius: "7px",

    fontSize: "9px",
    fontWeight: "750",

    cursor: "pointer"
};


//--------------------------------------------------------
// Empty
//--------------------------------------------------------

const emptyStyle =
{
    minHeight: "240px",

    display: "flex",
    flexDirection: "column",

    alignItems: "center",
    justifyContent: "center",

    textAlign: "center"
};


const emptyIconStyle =
{
    marginBottom: "12px",

    color: "#566274",

    fontSize: "24px"
};


const emptyTitleStyle =
{
    color: "#dce2ea",

    fontSize: "13px",
    fontWeight: "750"
};


const emptyTextStyle =
{
    marginTop: "5px",

    color: "#758195",

    fontSize: "10px"
};